import React, { ReactNode } from "react";
import { Avatar } from "primereact/avatar";
import TruncatedText from "app/uicomponents/microcomponents/TruncatedText";
import { cn } from "lib/utils";
import { getProfilePicBorderStyle } from "./utils/profilePictureBorder";

/**
 * `avatarClassName` for an initials avatar drawn like `ProfileLogo`'s: gray chip, Conthrax
 * initials (the dark theme redefines the gray palette). Add the size, e.g. `!text-[12px]`.
 */
export const INITIALS_AVATAR_CLASS_NAME = "conthrax-font font-medium";

interface AvatarWithNameProps {
  imageUrl?: string;
  name: string;
  avatarSize?: number;
  shape?: "circle" | "square";
  /**
   * Override for avatar label when no image (e.g. first name initial).
   * Falls back to first char of name.
   */
  initial?: string;
  /** Inline styles for the avatar (e.g. border color); merged over the default ring. */
  avatarStyle?: React.CSSProperties;
  /** Classes for the avatar, e.g. `INITIALS_AVATAR_CLASS_NAME` and the initials' font size. */
  avatarClassName?: string;
  /**
   * User type behind the picture (`admin`/`system` = IAPP case manager, anything else
   * = client/advisor). Drives the default ring color; ignored when `avatarStyle`
   * already sets a border.
   */
  userType?: string | null;
  nameClassName?: string;
  /**
   * Beside the name on the same line, e.g. a small gray detail. The name keeps its width and
   * the addon truncates first. For a line under the name use `subText`.
   */
  nameAddon?: ReactNode;
  /**
   * Line under the name: a string (e.g. "Age 45 · Preferred") or JSX when it needs an
   * icon or a button (e.g. an edit pencil). 12px secondary text unless `subTextClassName`
   * says otherwise.
   */
  subText?: ReactNode;
  subTextClassName?: string;
  /**
   * Optional full value used for tooltip rendering when `name` is truncated for display.
   */
  tooltipText?: string;
  className?: string;
}

/**
 * Avatar (picture, else initials, with the profile ring) beside a person's name, truncated
 * with a tooltip, and an optional sub-text line. Used across the review centers, subscriber
 * lists and Create Proposal's insured and client rows.
 */
const AvatarWithName: React.FC<AvatarWithNameProps> = ({
  imageUrl,
  name,
  avatarSize = 36,
  shape = "circle",
  initial,
  avatarStyle,
  avatarClassName,
  userType,
  nameClassName = "",
  nameAddon,
  subText,
  subTextClassName,
  tooltipText,
  className = "",
}) => {
  const displayName = name != null && typeof name === "string" ? name : String(name ?? "");
  const label = initial ?? displayName?.charAt(0)?.toUpperCase();
  // Every profile picture in IAPP carries a 1px ring: gray for clients/advisors, IAPP
  // blue for case managers. Call sites can still override it via `avatarStyle`.
  const borderStyle = getProfilePicBorderStyle(userType);
  const hasSubText = subText != null && subText !== "";
  const nameText = (
    <TruncatedText
      text={displayName || "-"}
      tooltipText={tooltipText ?? (displayName || "-")}
      className={cn(nameClassName, "block", nameAddon && "flex-shrink-0")}
      maxWidth="max-w-full"
      tooltipId={`avatar-with-name-${displayName}-${React?.useId()}`}
    />
  );
  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      <Avatar
        image={imageUrl}
        shape={shape}
        label={label}
        className={avatarClassName}
        style={{ width: avatarSize, height: avatarSize, ...borderStyle, ...avatarStyle }}
      />

      <div className="min-w-0 flex-1">
        {nameAddon ? (
          <div className="flex min-w-0 items-baseline gap-[8px]">
            {nameText}
            <span className="min-w-0 truncate">{nameAddon}</span>
          </div>
        ) : (
          nameText
        )}
        {hasSubText && (
          <div
            className={cn(
              "mt-[2px] text-[12px] leading-[16px] text-neutral-secondary-text",
              subTextClassName,
            )}
          >
            {subText}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarWithName;
